import {
    CrossRegionPersistence,
} from "@brightagency/headstart-common";
import * as qs from "query-string";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { siteData } from "../../../constants";
import { WPPost } from "../../../constants/types";
import {
    GenderExpressionFE,
    ListingAcf,
    NSWTBProcessLocalServices,
} from "../../../constants/types/acf";
import { useBaseUrl } from "../../../services/app-state";
import { useAssessmentState } from "../../../services/assessment-state";
import { fetchJSON } from "../../../utils/ajax";

type AssessmentResultResponse = {
    general: WPPost<ListingAcf>[];
    regional_service: WPPost<ListingAcf>[];
    digital_service: WPPost<ListingAcf>[];
    related: { title: string; href: string }[];
    content: { title: string; title_self: string };
    communities: [];
};

type AssessmentRequestData = {
    age_min: string;
    age_max: string;
    gender_identity: GenderExpressionFE | undefined;
    conditions: string;
    lgbtq: boolean;
    atsi: boolean;
    multicultural: boolean;
    specific_to_gender: boolean;
    is_carer: boolean;
    choosensuburb: string| undefined;
    communities: string | undefined;
};

export const fetchAssessmentResults = (
    baseUrl: string,
    payload: AssessmentRequestData,
): Promise<AssessmentResultResponse> => {
    const queryString = qs.stringify(payload);
    return fetchJSON(baseUrl + `/wp-json/headstart/v1/assessment-v2?${queryString}`);
};

export const useAssessmentResults = () => {
    const baseUrl = useBaseUrl();
    const { state } = useAssessmentState();
    const suburb = CrossRegionPersistence.getChosenSuburb()?.regionname;
    const query = useQuery(["assessment-v2-results", state], () =>
        fetchAssessmentResults(baseUrl, {            
            age_min: state.age,
            age_max: state.age,
            gender_identity: state.gender_identity,
            conditions: state.conditions.join(","),
            specific_to_gender: Boolean(state.specific_to_gender),
            is_carer: Boolean(state.target === "SOMEONE_ELSE"),
            choosensuburb: suburb,
            communities: state.active_community?.toString() ?? state.communities.join(","),
            multicultural: Boolean(state.multicultural),
            lgbtq: Boolean(state.lgbtq),
            atsi: Boolean(state.atsi),
        }),
    );

    const hasSecondaryResults = state.target === "SOMEONE_ELSE";

    return {
        ...query,
        hasSecondaryResults,
    };
};

const getMappedGenderTerm = (termId: number | false): GenderExpressionFE | undefined => {
    const genderMappings = siteData.acf.mappings.gender_mappings;

    switch (termId) {
        case genderMappings.male:
            return GenderExpressionFE.Male;
        case genderMappings.female:
            return GenderExpressionFE.Female;
        case genderMappings.different_identity:
            return GenderExpressionFE.DifferentIdentity;
        case genderMappings.non_binarygender_fluid:
            return GenderExpressionFE.NonBinaryOrGenderFluid;
    }

    return undefined;
};

const MapNSWTBProcessRequestData = (data: NSWTBProcessLocalServices): AssessmentRequestData => {
    const mappedGender = getMappedGenderTerm(data.gender);
    const suburb = CrossRegionPersistence.getChosenSuburb()?.regionname;
    
    return {
        age_min: data.mininum_age,
        age_max: data.maximum_age,
        gender_identity: mappedGender,
        conditions: (data.conditions !== false ? data.conditions : []).join(","),
        specific_to_gender: data.specific_to_gender,
        is_carer: false,
        choosensuburb: suburb,
        communities: data.active_community ?? Array.isArray(data.communities) ? data.communities?.join(',') : '',
        lgbtq: data.lgbtq,
        atsi: data.atsi,
        multicultural: data.multicultural,
    };
};

export const useNSWTBProcessLocalServices = (data: NSWTBProcessLocalServices) => {
    const baseUrl = useBaseUrl();
    const mappedRequestData = useMemo(() => MapNSWTBProcessRequestData(data), [data]);
    
    return useQuery(["population-page-assessment-results", mappedRequestData], {
        queryFn: () => fetchAssessmentResults(baseUrl, mappedRequestData),
        keepPreviousData: true,
    });
};
